class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));

    this.summaryToggle.setAttribute('role', 'button');

    /* Back must land on the previous page, not on this modal.

       Safari on iOS restores a page from its back/forward cache with the DOM
       exactly as it was left. Open the search, type, submit, then press Back and
       the page comes back with <details> still open, the predictive results still
       inside it, and <body> still carrying overflow-hidden — so Back appeared to
       return to the suggested-search dropdown rather than to the page before it,
       over a page that could no longer scroll.

       This has to be a pageshow listener rather than anything in the constructor:
       on a bfcache restore the element is the SAME element and the constructor
       does not run again. `persisted` is what distinguishes that restore from an
       ordinary load, where nothing is open in the first place.

       The predictive results are cleared as well as hidden. They were rendered
       for a search that has already been made and navigated away from, and a
       restored result list is exactly the kind of stale markup — images included —
       that reappears looking half-broken when the modal is next opened. */
    this.onPageShow = (event) => {
      if (!event.persisted || !this.isOpen()) return;
      this.close(false);
      const search = this.querySelector('predictive-search');
      if (search && typeof search.close === 'function') search.close(true);
    };
    window.addEventListener('pageshow', this.onPageShow);
  }

  /* Taken off again when the element goes. <details-modal> lives in the header
     SECTION, which the Theme Editor rebuilds on every edit — without this, each
     rebuild left another listener on window holding a dead modal and its whole
     predictive-search subtree alive, and a later bfcache restore ran all of them
     against detached nodes. */
  disconnectedCallback() {
    window.removeEventListener('pageshow', this.onPageShow);
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
  }
}

customElements.define('details-modal', DetailsModal);
